import { useState } from "react";
import {
  Badge,
  Popover,
  List,
  Avatar,
  Button,
  Empty,
  Typography,
  Tooltip,
  message,
} from "antd";
import {
  BellOutlined,
  CopyOutlined,
  ShoppingCartOutlined,
  ProfileOutlined,
  MessageOutlined,
  SwapOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import { isNewTabClick, useOpenRoute } from "../../Hooks/useRouteLink";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  NotificationKind,
  useNotificationsStore,
} from "../../Socket/notificationsStore";
import "./style.scss";

dayjs.extend(relativeTime);

const kindMeta: Record<
  NotificationKind,
  { icon: JSX.Element; color: string }
> = {
  order: { icon: <ShoppingCartOutlined />, color: "light-dark(#28a745, #73dd8b)" },
  activity: { icon: <ProfileOutlined />, color: "light-dark(#1a7ffd, #4da6ff)" },
  reply: { icon: <MessageOutlined />, color: "light-dark(#17a2b8, #65d9ec)" },
  status: { icon: <SwapOutlined />, color: "#faad14" },
  warning: { icon: <ExclamationCircleOutlined />, color: "#fd7e14" },
  missing: { icon: <QuestionCircleOutlined />, color: "#fd7e14" },
  import: { icon: <CloudUploadOutlined />, color: "light-dark(#722ed1, #9f71e0)" },
  error: { icon: <CloseCircleOutlined />, color: "light-dark(#dc3545, #e56b77)" },
};

export const NotificationCenter = () => {
  const { t } = useTranslation();
  const openRoute = useOpenRoute();
  const { items, unreadCount, markRead, markAllRead, clearAll } =
    useNotificationsStore();
  const [open, setOpen] = useState(false);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Opening the panel clears the unread badge — matches the old CRM box.
    if (next && unreadCount > 0) markAllRead();
  };

  const handleRowClick = (
    event: React.MouseEvent,
    id: string,
    path?: string | null
  ) => {
    markRead(id);
    // Keep the panel open after a new-tab click so more rows can be opened.
    if (!isNewTabClick(event)) setOpen(false);
    if (path) openRoute(path, event);
  };

  // stopPropagation so copying never triggers the row's navigate/close.
  const handleCopyId = async (
    event: React.MouseEvent,
    copyId: string | number
  ) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(copyId));
      message.success(
        t("notifications.center.idCopied", "ID copied: {{id}}", { id: copyId })
      );
    } catch {
      message.error(t("notifications.center.idCopyFailed", "Failed to copy ID"));
    }
  };

  const content = (
    <div className="notification-center-panel">
      <div className="ncp-header">
        <Typography.Text strong>
          {t("notifications.center.title", "Notifications")}
        </Typography.Text>
        {items.length > 0 && (
          <div className="ncp-actions">
            <Button type="link" size="small" onClick={() => markAllRead()}>
              {t("notifications.center.markAllRead", "Mark all read")}
            </Button>
            <Button type="link" size="small" danger onClick={() => clearAll()}>
              {t("notifications.center.clearAll", "Clear all")}
            </Button>
          </div>
        )}
      </div>

      <div className="ncp-body">
        {items.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t(
              "notifications.center.empty",
              "No notifications yet"
            )}
          />
        ) : (
          <List
            dataSource={items}
            renderItem={(item) => {
              const meta = kindMeta[item.kind];
              return (
                <List.Item
                  className={`ncp-item ${item.read ? "read" : "unread"} ${
                    item.path ? "clickable" : ""
                  }`}
                  onClick={(event) => handleRowClick(event, item.id, item.path)}
                  extra={
                    item.copyId ? (
                      <Tooltip
                        title={t(
                          "notifications.center.copyId",
                          "Copy ID {{id}}",
                          { id: item.copyId }
                        )}
                      >
                        <Button
                          className="ncp-item-copy"
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          aria-label={t(
                            "notifications.center.copyId",
                            "Copy ID {{id}}",
                            { id: item.copyId }
                          )}
                          onClick={(event) =>
                            handleCopyId(event, item.copyId as string | number)
                          }
                        />
                      </Tooltip>
                    ) : null
                  }
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{
                          backgroundColor: `${meta.color}1a`,
                          color: meta.color,
                        }}
                        icon={meta.icon}
                      />
                    }
                    title={
                      <span className="ncp-item-title">{item.title}</span>
                    }
                    description={
                      <>
                        <div className="ncp-item-body">{item.body}</div>
                        <div className="ncp-item-time">
                          {dayjs(item.createdAt).fromNow()}
                        </div>
                      </>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={handleOpenChange}
      overlayClassName="notification-center-popover"
      getPopupContainer={(node) => node.parentNode as HTMLElement}
    >
      <button
        type="button"
        className="notification-bell-btn"
        aria-label={t("notifications.center.title", "Notifications")}
      >
        <Badge count={unreadCount} size="small" overflowCount={99}>
          <BellOutlined />
        </Badge>
      </button>
    </Popover>
  );
};
